import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import ErrorBoundary from "@/components/ErrorBoundary";
import { registerServiceWorker } from "@/lib/pwaInstall";
import { init as initPersistentStorage } from "@/lib/storage";

// ---------------------------------------------------------------------------
// Global fetch() patch for cross-origin auth.
//
// Background: many older pages use raw `fetch(url, { credentials: 'include' })`
// expecting the browser to attach the auth cookie. On custom domains (e.g.
// elloraepc.in calling ellora-erp-mvp-1.emergent.host) the platform proxy
// returns `Access-Control-Allow-Origin: *` which FORCES the browser to:
//   (a) drop credentials (cookies),  AND
//   (b) reject the entire request with a CORS error when `credentials:
//       'include'` is explicitly requested with a `*` origin response.
//
// Fix: for every fetch() call to our backend we:
//   1. Inject `Authorization: Bearer <token>` from localStorage (where login
//      persists it) if no Authorization header is already set.
//   2. Force `credentials: 'omit'` so the browser stops trying to attach
//      cookies — Bearer token now replaces them.
// Other fetches (third-party APIs, CDNs) are untouched.
// ---------------------------------------------------------------------------
(function patchFetchForAuth() {
  if (typeof window === "undefined" || !window.fetch || window.__fetchAuthPatched) return;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const originalFetch = window.fetch.bind(window);
  window.fetch = function patchedFetch(input, init) {
    try {
      const url = typeof input === "string" ? input : (input && input.url) || "";
      const isOurBackend = BACKEND_URL && url.startsWith(BACKEND_URL);
      if (isOurBackend) {
        init = init || {};
        const headers = new Headers(init.headers || (typeof input !== "string" && input.headers) || {});
        const token = localStorage.getItem("access_token");
        if (token && !headers.has("Authorization")) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        init.headers = headers;
        // strip credentials so the request becomes a simple
        // CORS request and the browser stops requiring a non-wildcard
        // Access-Control-Allow-Origin. Bearer token handles auth.
        init.credentials = "omit";
      }
    } catch (_) { /* never break the call chain */ }
    return originalFetch(input, init);
  };
  window.__fetchAuthPatched = true;
})();

registerServiceWorker();

const root = ReactDOM.createRoot(document.getElementById("root"));

// Hydrate localStorage from native Capacitor Preferences BEFORE App mounts.
// On mobile, this restores the JWT that survived the app update.
initPersistentStorage().finally(() => {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>,
  );
});
